import { useEffect, useRef, useState } from "react";
import { useLoop } from "@/pages/home/LoopContext";

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// ループ段階別のシーンコンテンツ
function getExperienceContent(loopCount: number, isUnlocked: boolean) {
  if (isUnlocked) {
    return {
      label: "Transformation",
      title: "光の変容",
      footer: "触れるだけで、すべてが変わる。",
      accentColor: "rgba(255,220,140,",
      scenes: [
        {
          before: "封印の闇",
          after: "解放の光",
          image: "https://public.readdy.ai/ai/img_res/46fd0546420ac091a2750d9aff566a79.jpg",
          afterImage: "https://readdy.ai/api/search-image?query=golden%20divine%20light%20flooding%20through%20open%20ancient%20door%2C%20warm%20celestial%20radiance%2C%20sacred%20space%20revealed%2C%20spiritual%20awakening%20atmosphere%2C%20cinematic%20photography&width=600&height=800&seq=exp_open01&orientation=portrait",
        },
        {
          before: "螺旋の試練",
          after: "覚醒の証",
          image: "https://readdy.ai/api/search-image?query=dark%20spiral%20staircase%20descending%20into%20void%2C%20ominous%20deep%20shadow%2C%20mysterious%20ancient%20stone%20architecture%2C%20cinematic%20dark%20photography&width=600&height=800&seq=exp_spiral01&orientation=portrait",
          afterImage: "https://readdy.ai/api/search-image?query=glowing%20crystal%20mineral%20stone%20in%20golden%20divine%20light%2C%20inner%20radiance%20emanating%2C%20sacred%20spiritual%20object%2C%20warm%20amber%20glow%2C%20cinematic%20macro%20photography&width=600&height=800&seq=exp_crystal01&orientation=portrait",
        },
      ],
    };
  }

  if (loopCount === 0) {
    return {
      label: "Experience",
      title: "変容の扉",
      footer: "触れるだけで、何かが変わる。",
      accentColor: "rgba(201,168,76,",
      scenes: [
        {
          before: "騒がしい日常",
          after: "静寂の中心",
          image: "https://readdy.ai/api/search-image?query=chaotic%20busy%20modern%20city%20life%20blurred%20motion%2C%20dark%20moody%20cinematic%20photography%2C%20deep%20shadows%2C%20monochrome%20with%20slight%20warm%20tone%2C%20abstract%20feeling%20of%20noise%20and%20overwhelm%2C%20artistic%20blur&width=600&height=800&seq=scene001&orientation=portrait",
          afterImage: "https://readdy.ai/api/search-image?query=serene%20zen%20meditation%20space%20with%20single%20candle%20light%2C%20deep%20darkness%20surrounding%2C%20minimal%20sacred%20atmosphere%2C%20smoke%20and%20incense%2C%20cinematic%20golden%20light%20in%20darkness%2C%20spiritual%20calm%2C%20ultra%20minimal&width=600&height=800&seq=scene002&orientation=portrait",
        },
        {
          before: "表面の自分",
          after: "魂の深層",
          image: "https://readdy.ai/api/search-image?query=person%20wearing%20mask%20metaphor%2C%20dark%20moody%20portrait%20photography%2C%20deep%20shadows%2C%20cinematic%20lighting%2C%20mysterious%20atmosphere%2C%20black%20background%2C%20artistic%20conceptual%20photography&width=600&height=800&seq=scene003&orientation=portrait",
          afterImage: "https://readdy.ai/api/search-image?query=glowing%20crystal%20mineral%20stone%20in%20darkness%2C%20inner%20light%20emanating%20from%20within%2C%20deep%20black%20background%2C%20golden%20amber%20glow%2C%20spiritual%20sacred%20object%2C%20cinematic%20macro%20photography&width=600&height=800&seq=scene004&orientation=portrait",
        },
      ],
    };
  }

  if (loopCount <= 2) {
    return {
      label: "Resonance",
      title: "螺旋の共鳴",
      footer: "何度戻っても、それは間違いではない。",
      accentColor: "rgba(201,168,76,",
      scenes: [
        {
          before: "繰り返す日々",
          after: "螺旋の意味",
          image: "https://readdy.ai/api/search-image?query=repetitive%20clock%20faces%20overlapping%2C%20time%20loop%20concept%2C%20dark%20moody%20abstract%20photography%2C%20deep%20shadows%2C%20monochrome%20cinematic&width=600&height=800&seq=exp_loop01&orientation=portrait",
          afterImage: "https://readdy.ai/api/search-image?query=golden%20spiral%20galaxy%20nebula%20in%20deep%20space%2C%20cosmic%20swirl%20of%20light%2C%20spiritual%20universe%2C%20warm%20amber%20glow%20in%20darkness%2C%20cinematic%20space%20photography&width=600&height=800&seq=exp_spiral02&orientation=portrait",
        },
        {
          before: "引力の正体",
          after: "宇宙の意思",
          image: "https://readdy.ai/api/search-image?query=dark%20magnetic%20field%20visualization%2C%20invisible%20force%20pulling%2C%20abstract%20dark%20physics%20concept%2C%20deep%20shadow%20cinematic%20photography&width=600&height=800&seq=exp_pull01&orientation=portrait",
          afterImage: "https://readdy.ai/api/search-image?query=cosmic%20energy%20convergence%20point%2C%20golden%20light%20gathering%20at%20center%2C%20spiritual%20force%20manifestation%2C%20dark%20background%20with%20warm%20glow%2C%20cinematic%20photography&width=600&height=800&seq=exp_cosmos01&orientation=portrait",
        },
      ],
    };
  }

  if (loopCount <= 4) {
    return {
      label: "Deepening · 深化",
      title: "深淵への降下",
      footer: "深く潜るほど、真実が見える。",
      accentColor: "rgba(210,130,50,",
      scenes: [
        {
          before: "表層の恐れ",
          after: "守護の光",
          image: "https://readdy.ai/api/search-image?query=dark%20abyss%20void%20looking%20down%20into%20endless%20darkness%2C%20fear%20of%20depth%2C%20ominous%20deep%20shadow%2C%20cinematic%20dark%20photography%2C%20no%20people&width=600&height=800&seq=exp_fear01&orientation=portrait",
          afterImage: "https://readdy.ai/api/search-image?query=protective%20golden%20light%20shield%20in%20darkness%2C%20divine%20guardian%20energy%2C%20warm%20amber%20radiance%20surrounding%2C%20spiritual%20protection%2C%20cinematic%20photography&width=600&height=800&seq=exp_guard01&orientation=portrait",
        },
        {
          before: "試練の螺旋",
          after: "覚醒の予兆",
          image: "https://readdy.ai/api/search-image?query=dark%20spiral%20staircase%20descending%20into%20void%2C%20ominous%20deep%20shadow%2C%20mysterious%20ancient%20stone%20architecture%2C%20cinematic%20dark%20photography&width=600&height=800&seq=exp_stair01&orientation=portrait",
          afterImage: "https://readdy.ai/api/search-image?query=single%20ray%20of%20golden%20light%20breaking%20through%20darkness%2C%20dawn%20of%20awakening%2C%20spiritual%20illumination%2C%20minimal%20cinematic%20photography%2C%20warm%20glow&width=600&height=800&seq=exp_dawn01&orientation=portrait",
        },
      ],
    };
  }

  // 5回以上（奈落直前）
  return {
    label: "Abyss · 奈落",
    title: "奈落の縁",
    footer: "次の一歩が、すべてを変える。",
    accentColor: "rgba(200,70,30,",
    scenes: [
      {
        before: "奈落の口",
        after: "光の核",
        image: "https://readdy.ai/api/search-image?query=dark%20abyss%20edge%20cliff%20overlooking%20endless%20void%20below%2C%20ominous%20red%20crack%20glowing%20in%20darkness%2C%20ancient%20stone%20with%20crimson%20light%2C%20foreboding%20deep%20chasm%2C%20no%20people%2C%20ultra%20dark%20cinematic%20photography&width=600&height=800&seq=exp_abyss01&orientation=portrait",
        afterImage: "https://readdy.ai/api/search-image?query=brilliant%20white%20golden%20light%20core%20at%20center%20of%20darkness%2C%20divine%20energy%20source%2C%20spiritual%20revelation%2C%20intense%20radiance%20in%20void%2C%20cinematic%20photography&width=600&height=800&seq=exp_core01&orientation=portrait",
      },
      {
        before: "最後の試練",
        after: "真実の扉",
        image: "https://readdy.ai/api/search-image?query=ancient%20sealed%20stone%20door%20with%20red%20glowing%20cracks%2C%20forbidden%20entrance%2C%20ominous%20crimson%20light%20seeping%20through%2C%20dark%20mysterious%20atmosphere%2C%20cinematic%20photography&width=600&height=800&seq=exp_door01&orientation=portrait",
        afterImage: "https://readdy.ai/api/search-image?query=sacred%20vault%20door%20opening%20to%20reveal%20golden%20divine%20light%2C%20revelation%20moment%2C%20spiritual%20gateway%2C%20warm%20celestial%20radiance%20flooding%20through%2C%20cinematic%20photography&width=600&height=800&seq=exp_reveal01&orientation=portrait",
      },
    ],
  };
}

export default function RitualExperience() {
  const { ref, inView } = useInView(0.15);
  const { loopCount, isUnlocked } = useLoop();
  const content = getExperienceContent(loopCount, isUnlocked);
  const isNearAbyss = loopCount >= 4 && !isUnlocked;

  return (
    <section className="relative bg-[#0a0809] py-20 md:py-40 px-4 md:px-6 overflow-hidden">
      {/* 上部ライン */}
      <div
        className="absolute left-1/2 top-0 -translate-x-1/2 w-px h-24"
        style={{ background: `linear-gradient(to bottom, transparent, ${content.accentColor}0.2))` }}
      />

      {/* 深淵近く：赤いオーバーレイ */}
      {isNearAbyss && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at 50% 100%, rgba(40,0,0,0.25) 0%, transparent 70%)",
          }}
        />
      )}

      <div ref={ref} className="max-w-5xl mx-auto">
        {/* ヘッダー */}
        <div
          className="text-center mb-12 md:mb-24"
          style={{ opacity: inView ? 1 : 0, transition: "opacity 1.5s ease" }}
        >
          <div
            className="text-xs tracking-[0.4em] md:tracking-[0.5em] uppercase mb-4 md:mb-6"
            style={{ color: `${content.accentColor}0.4)`, transition: "color 1.5s ease" }}
          >
            {content.label}
          </div>
          <h2
            className="text-2xl md:text-4xl font-light mb-2"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: isNearAbyss ? "rgba(220,100,60,0.8)" : "rgba(255,255,255,0.8)",
              transition: "color 1.5s ease",
            }}
          >
            {content.title}
          </h2>
        </div>

        {/* シーングリッド */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-24">
          {content.scenes.map((scene, i) => (
            <div
              key={`${loopCount}-${i}`}
              className="group"
              style={{
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(30px)",
                transition: `opacity 1.5s ease ${i * 0.3}s, transform 1.5s ease ${i * 0.3}s`,
              }}
            >
              <div className="relative h-56 md:h-96 overflow-hidden mb-6 md:mb-8">
                {/* Before */}
                <div
                  className="absolute inset-0 bg-cover bg-top transition-opacity duration-1000 group-hover:opacity-0"
                  style={{
                    backgroundImage: `url(${scene.image})`,
                    filter: isNearAbyss
                      ? "brightness(0.4) saturate(0.3) hue-rotate(-10deg)"
                      : "brightness(0.5) saturate(0.4)",
                  }}
                />
                {/* After */}
                <div
                  className="absolute inset-0 bg-cover bg-top opacity-0 transition-opacity duration-1000 group-hover:opacity-100"
                  style={{
                    backgroundImage: `url(${scene.afterImage})`,
                    filter: isUnlocked
                      ? "brightness(0.7) saturate(0.9)"
                      : "brightness(0.6) saturate(0.7)",
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0809] via-transparent to-transparent" />

                {/* ラベル */}
                <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                  <span
                    className="text-xs tracking-[0.3em] transition-opacity duration-700 group-hover:opacity-0"
                    style={{
                      color: isNearAbyss ? "rgba(200,80,40,0.4)" : "rgba(255,255,255,0.3)",
                      fontFamily: "'Cormorant Garamond', serif",
                    }}
                  >
                    {scene.before}
                  </span>
                  <span
                    className="text-xs tracking-[0.3em] opacity-0 transition-opacity duration-700 group-hover:opacity-100"
                    style={{
                      color: `${content.accentColor}0.7)`,
                      fontFamily: "'Cormorant Garamond', serif",
                    }}
                  >
                    {scene.after}
                  </span>
                </div>
              </div>

              {/* 区切り */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.05)" }} />
                <div style={{ color: `${content.accentColor}0.3)`, fontSize: "10px" }}>✦</div>
                <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.05)" }} />
              </div>
            </div>
          ))}
        </div>

        {/* フッター */}
        <div
          className="text-center mt-12 md:mt-24"
          style={{ opacity: inView ? 1 : 0, transition: "opacity 2s ease 0.8s" }}
        >
          <p
            className="text-base tracking-[0.2em]"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: isNearAbyss ? "rgba(200,80,40,0.25)" : "rgba(255,255,255,0.2)",
              transition: "color 1.5s ease",
            }}
          >
            {content.footer}
          </p>
        </div>
      </div>
    </section>
  );
}
